import React, { useState, useEffect, useCallback } from 'react';
import { analyticsService } from '../services/analyticsApi';
import { Eye, MessageCircle, TrendingUp, Calendar, ChevronDown, ChevronUp, BarChart3, RefreshCw } from 'lucide-react';

const DAY_NAMES = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'];

const StatCard = ({ icon: Icon, label, value, sublabel, color }) => (
  <div className={`bg-white rounded-xl p-4 border ${color} shadow-sm`}>
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${color.replace('border-', 'bg-').replace('200', '50')}`}>
        <Icon className={`w-5 h-5 ${color.replace('border-', 'text-').replace('200', '600')}`} />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-xs text-gray-500">{label}</p>
      </div>
    </div>
    {sublabel && <p className="text-xs text-gray-400 mt-2">{sublabel}</p>}
  </div>
);

export const AdminStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      const data = await analyticsService.getStats(token);
      setStats(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadStats();
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return `${DAY_NAMES[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1}`;
  };

  const formatLastClick = (isoStr) => {
    if (!isoStr) return '-';
    const d = new Date(isoStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `Hace ${diffMins}min`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `Hace ${diffHours}h`;
    const diffDays = Math.floor(diffHours / 24);
    return `Hace ${diffDays}d`;
  };

  if (loading) {
    return (
      <div data-testid="admin-stats-loading" className="bg-gradient-to-r from-slate-50 to-gray-50 rounded-2xl p-6 mb-8 border border-slate-200 animate-pulse">
        <div className="h-6 bg-slate-200 rounded w-48 mb-4"></div>
        <div className="grid grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-20 bg-slate-200 rounded-xl"></div>)}
        </div>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div data-testid="admin-stats-panel" className="bg-gradient-to-r from-slate-50 to-gray-50 rounded-2xl p-6 mb-8 border border-slate-200 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          data-testid="stats-toggle-btn"
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 text-slate-800 hover:text-slate-900 transition-colors"
        >
          <BarChart3 className="w-5 h-5" />
          <h3 className="text-lg font-bold">Panel de Estadisticas</h3>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        <button
          data-testid="stats-refresh-btn"
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {expanded && (
        <div className="space-y-6">
          {/* Visit Stats */}
          <div>
            <h4 className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Visitas al Catalogo
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard icon={Eye} label="Hoy" value={stats.visits.today} color="border-blue-200" />
              <StatCard icon={Calendar} label="Esta semana" value={stats.visits.week} color="border-indigo-200" />
              <StatCard icon={TrendingUp} label="Este mes" value={stats.visits.month} color="border-purple-200" />
              <StatCard icon={BarChart3} label="Total" value={stats.visits.total} color="border-slate-200" />
            </div>

            {/* Daily visits table */}
            {stats.visits.daily && stats.visits.daily.length > 0 && (
              <div className="mt-3 bg-white rounded-xl border border-slate-100 overflow-hidden">
                <div className="px-4 py-2 bg-slate-50 border-b border-slate-100">
                  <p className="text-xs font-medium text-slate-500">Ultimos 7 dias</p>
                </div>
                <div className="flex divide-x divide-slate-100">
                  {stats.visits.daily.map(day => (
                    <div key={day.date} className="flex-1 px-3 py-2 text-center">
                      <p className="text-lg font-bold text-slate-800">{day.count}</p>
                      <p className="text-xs text-slate-400">{formatDate(day.date)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* WhatsApp Stats */}
          <div>
            <h4 className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-3 flex items-center gap-2">
              <MessageCircle className="w-4 h-4" />
              Consultas por WhatsApp
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <StatCard icon={MessageCircle} label="Hoy" value={stats.whatsapp.today} color="border-emerald-200" />
              <StatCard icon={Calendar} label="Esta semana" value={stats.whatsapp.week} color="border-green-200" />
              <StatCard icon={TrendingUp} label="Este mes" value={stats.whatsapp.month} color="border-teal-200" />
              <StatCard icon={BarChart3} label="Total" value={stats.whatsapp.total} color="border-slate-200" />
            </div>

            {/* WhatsApp clicks by puppy table */}
            {stats.whatsapp.by_puppy && stats.whatsapp.by_puppy.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
                <div className="px-4 py-2 bg-slate-50 border-b border-slate-100">
                  <p className="text-xs font-medium text-slate-500">Cachorros con mas interes (clics en WhatsApp)</p>
                </div>
                <table data-testid="whatsapp-clicks-table" className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100 text-left">
                      <th className="px-4 py-2 text-xs font-medium text-slate-500">#</th>
                      <th className="px-4 py-2 text-xs font-medium text-slate-500">Cachorro</th>
                      <th className="px-4 py-2 text-xs font-medium text-slate-500">Raza</th>
                      <th className="px-4 py-2 text-xs font-medium text-slate-500 text-center">Clics</th>
                      <th className="px-4 py-2 text-xs font-medium text-slate-500 text-right">Ultimo clic</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.whatsapp.by_puppy.map((item, idx) => (
                      <tr key={item.puppy_id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-2.5 text-sm text-slate-400">{idx + 1}</td>
                        <td className="px-4 py-2.5 text-sm font-medium text-slate-800">{item.puppy_name}</td>
                        <td className="px-4 py-2.5 text-sm text-slate-600 capitalize">{item.breed}</td>
                        <td className="px-4 py-2.5 text-center">
                          <span className="inline-flex items-center justify-center bg-emerald-100 text-emerald-700 font-bold text-sm rounded-full w-8 h-8">
                            {item.total_clicks}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-sm text-slate-400 text-right">{formatLastClick(item.last_click)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {stats.whatsapp.by_puppy && stats.whatsapp.by_puppy.length === 0 && (
              <div className="bg-white rounded-xl border border-slate-100 p-6 text-center">
                <MessageCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Aun no hay consultas por WhatsApp registradas</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
